import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import * as contentEN from '../data/mockData';
import * as contentSL from '../data/content_sl';

const LanguageContext = createContext({ lang: 'en', prefix: '' });

// Slug mapping for hreflang alternates
const EN_TO_SL_SLUGS = {
  '/about/': '/o-nas/',
  '/services/': '/storitve/',
  '/projects/': '/projekti/',
};

const SL_TO_EN_SLUGS = Object.fromEntries(
  Object.entries(EN_TO_SL_SLUGS).map(([en, sl]) => [sl, en])
);

export const LanguageProvider = ({ children }) => {
  const location = useLocation();

  const value = useMemo(() => {
    const path = location.pathname;
    const isSL = path.startsWith('/sl/') || path === '/sl';
    return {
      lang: isSL ? 'sl' : 'en',
      prefix: isSL ? '/sl' : '',
    };
  }, [location.pathname]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export const useContent = () => {
  const { lang } = useLanguage();
  return lang === 'sl' ? contentSL : contentEN;
};

/**
 * Get the URL for switching between EN and SL.
 * Handles slug translation: /about/ <-> /o-nas/, etc.
 */
export const useSwitchLangHref = () => {
  const location = useLocation();
  const { lang } = useLanguage();

  return useMemo(() => {
    let path = location.pathname;
    if (!path.endsWith('/')) path = path + '/';

    if (lang === 'sl') {
      // SL -> EN: remove /sl/ prefix, then map SL slugs to EN slugs
      let stripped = path.replace(/^\/sl/, '') || '/';
      if (!stripped.endsWith('/')) stripped += '/';
      for (const [slSlug, enSlug] of Object.entries(SL_TO_EN_SLUGS)) {
        if (stripped === slSlug) {
          return enSlug;
        }
      }
      return stripped;
    } else {
      // EN -> SL: map EN slugs to SL slugs, then add /sl/ prefix
      let slSlug = path;
      for (const [enSlug, mappedSlSlug] of Object.entries(EN_TO_SL_SLUGS)) {
        if (path === enSlug) {
          slSlug = mappedSlSlug;
          break;
        }
      }
      return slSlug === '/' ? '/sl/' : `/sl${slSlug}`;
    }
  }, [location.pathname, lang]);
};

/**
 * Returns a localized path given an EN slug.
 * Usage: localPath('/about/') returns '/about/' in EN or '/o-nas/' in SL
 * Automatically prepends the language prefix.
 */
export const useLocalPath = () => {
  const { lang, prefix } = useLanguage();

  return (enSlug) => {
    if (lang === 'sl') {
      const slSlug = EN_TO_SL_SLUGS[enSlug] || enSlug;
      return `${prefix}${slSlug}`;
    }
    return enSlug;
  };
};

// UI labels hook — returns SL ui labels or sensible EN defaults
export const useUI = () => {
  const { lang } = useLanguage();
  if (lang === 'sl') return contentSL.ui;
  // English defaults
  return {
    seeOurWork: 'SEE OUR WORK',
    statusBadge: 'Platform Builder \u2014 Active',
    seeOurPlatforms: 'See Our Platforms',
    howWeThink: 'How We Think',
    scroll: 'Scroll',
    whatWeDoLabel: 'What We Do',
    buildOperateScale: ['Build.', 'Operate.', 'Scale.'],
    whatWeDoDescription: "We don't run campaigns. We build digital platforms engineered for organic growth, affiliate monetization, and long-term value. Every platform is an asset we own and operate.",
    exploreAllServices: 'Explore all services',
    ourPlatformsLabel: 'Our Platforms :: Proof of Execution',
    weBuildTitle: ['We Build ', 'What We Preach'],
    projectsDescription: "These aren't client projects. These are platforms we own, operate, and grow. Real revenue. Real traffic. Real proof.",
    monthlyVisits: 'Monthly Visits',
    livePlatforms: 'Live Platforms',
    viewAllPlatforms: 'View all platforms',
    visit: 'Visit',
    contactLabel: 'Contact',
    letsBuild: "Let's Build Something",
    gotPlatformIdea: ['Got a ', 'Platform Idea?'],
    ctaDescription: "Whether you want to partner on a digital platform, explore affiliate opportunities, or discuss how we approach SEO and AI \u2014 we're listening.",
    startConversation: 'Start A Conversation',
    sendMessage: 'Send Message',
    emailDirectly: 'Email Directly:',
    interestedInApproach: 'Interested in our approach?',
    learnHowWeThink: 'Learn How We Think',
    copyright: '\u00A9 2025 Go2 Digital. Slovenia.',
    aboutLabel: 'About',
    aboutHeroTitle: ['Not an Agency.', 'A Platform Builder.'],
    ourStoryLabel: 'Our Story',
    fromBlogToEcosystem: ['From a Blog to ', 'an Ecosystem.'],
    corePhilosophyLabel: 'Core Philosophy',
    whyDifferentLabel: "Why We're Different",
    weThinkLikeOwners: ['We Think Like ', 'Owners.'],
    whyDifferentDesc: 'Because we are. Every strategy, every system, every decision is tested on our own platforms first.',
    theTeamLabel: 'The Team',
    smallTeamBigOutput: ['Small Team. ', 'Big Output.'],
    seeWhatWeBuilt: ["See What We've ", 'Built.'],
    viewPlatforms: 'View Platforms',
    servicesLabel: 'Services',
    whatWeActuallyDo: ['What We ', 'Actually Do.'],
    servicesDescription: "Four interconnected disciplines. Each one tested and refined on our own platforms before we ever talk about them. Not theory. Practice.",
    outcomes: 'Outcomes',
    realWorldProof: 'Real-World Proof',
    wantToSeeInAction: ['Want to see these systems ', 'in action?'],
    servicesCtaDesc: 'Every service we offer is battle-tested on our own platforms. Check out the results.',
    contactUs: 'Contact Us',
    projectsLabel: 'Projects :: Proof of Execution',
    ourPlatformsTitle: ['Our ', 'Platforms.'],
    projectsPageDesc: "These aren't client projects or portfolio pieces. These are platforms we built, own, and operate. Real traffic. Real revenue. Real proof that our approach works.",
    markets: 'Markets',
    keyResults: 'Key Results',
    ecosystemLabel: 'The Go2 Ecosystem',
    onePlaybook: ['One playbook. ', 'Multiple markets.'],
    ecosystemDesc: 'Every platform in our ecosystem shares the same infrastructure, the same SEO methodology, and the same monetization framework. What works on one platform gets deployed across all of them.',
    seeHowWeDoIt: 'See How We Do It',
    inDevelopment: 'In Development',
  };
};
